import { Grid, GridSize, Skeleton, SxProps, Theme } from '@mui/material';
import React, {
  FunctionComponent,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ConditionalField from '../../components/@material/ConditionalField';
import Element, {
  IAccessibilityConfigFb,
  ThirdPartySystemsComponentConfigFb,
} from '../formBuilder/formularElement';
import { magicImport } from './magicImport';
import { SlotVisibilityContext } from './slotVisibilityContext';
import {
  IValidateParams,
  ThirdPartySystemsConfigFb,
} from '../services/thirdPartySystemsService';

interface LazyComponentLoaderProps {
  refComponent: string;
  componentLoadingFnct?: Function;
  elements?: Element[];
  sm?: GridSize;
  xs?: GridSize;
  container?: boolean;
  fieldName?: string;
  accessibilityConfig?: Array<IAccessibilityConfigFb>;
  thirdPartySystemsConfig?:
    | Array<ThirdPartySystemsComponentConfigFb>
    | undefined;
  // components that parse their own child elements (e.g. the accordion) need
  // the form wide config to build the validation of those children
  formThirdPartySystemsConfig?: ThirdPartySystemsConfigFb;
  rulesConfig?: any;
  excludeGrid?: boolean;
  validateFn?: (props: IValidateParams) => void;
  sx?: SxProps<Theme>;
}

const customComponentLoadingFnct = (refComponent: string) =>
  React.lazy(magicImport(refComponent));

const LazyComponentLoader: FunctionComponent<LazyComponentLoaderProps> = ({
  refComponent,
  componentLoadingFnct = customComponentLoadingFnct,
  children,
  sm,
  xs,
  container,
  excludeGrid,
  fieldName,
  accessibilityConfig,
  thirdPartySystemsConfig,
  rulesConfig,
  sx,
  ...props
}) => {
  const [showParent, setShowParent] = useState(true); // show parent Grid unless child is hidden
  // a slot component (e.g. the accordion) may want to know when this element is hidden
  const reportSlotVisibility = useContext(SlotVisibilityContext);
  // without a rule the element is always visible, even if a former rule left showParent false
  const hasRule = Boolean(fieldName) && rulesConfig?.length > 0;
  const isVisible = !hasRule || showParent;
  useEffect(() => {
    reportSlotVisibility?.(isVisible);
    // an element that leaves its slot must not keep the slot hidden
    return () => reportSlotVisibility?.(true);
  }, [isVisible, reportSlotVisibility]);
  const CustomComponent: React.LazyExoticComponent<React.ComponentType<any>> =
    useMemo(() => componentLoadingFnct(refComponent), [refComponent]);

  let defaults = getDefaultLayoutValues(refComponent);
  xs = xs ?? (defaults.xs as GridSize);
  sm = sm ?? (defaults.sm as GridSize);
  container = container ?? defaults.container;

  const mergedProps = {
    ...props,
    accessibilityConfig,
    thirdPartySystemsConfig,
  };

  return (
    // nested elements report to nobody: only the element directly in a slot counts
    <SlotVisibilityContext.Provider value={undefined}>
      {excludeGrid ? (
        <Suspense fallback={<Skeleton width={'100%'} height={'50px'} />}>
          <CustomComponent {...props}>{children}</CustomComponent>
        </Suspense>
      ) : container ? (
        <Grid justifyContent="start" container spacing={0}>
          <Suspense fallback={<Skeleton width={'100%'} height={'50px'} />}>
            <CustomComponent {...props}>{children}</CustomComponent>
          </Suspense>
        </Grid>
      ) : fieldName && rulesConfig?.length > 0 ? (
        <Grid
          item
          xs={xs}
          sm={sm}
          sx={{
            ...sx,
            display: showParent ? undefined : 'none',
          }}
        >
          <ConditionalField
            fieldName={fieldName}
            rulesConfig={rulesConfig}
            elements={mergedProps?.elements}
            setParentVisible={(visible) => {
              setShowParent(visible);
            }}
          >
            <Suspense fallback={<Skeleton width={'100%'} height={'50px'} />}>
              <CustomComponent
                {...props}
                fieldName={fieldName}
                rulesConfig={rulesConfig}
                setParentVisible={(visible: boolean) => {
                  setShowParent(visible);
                }}
              >
                {children}
              </CustomComponent>
            </Suspense>
          </ConditionalField>
        </Grid>
      ) : (
        <Grid item xs={xs} sm={sm} sx={sx}>
          <Suspense fallback={<Skeleton width={'100%'} height={'50px'} />}>
            <CustomComponent {...mergedProps} fieldName={fieldName}>
              {children}
            </CustomComponent>
          </Suspense>
        </Grid>
      )}
    </SlotVisibilityContext.Provider>
  );
};

//TODO: sollte später in globale metainformationen verschoben werden
function getDefaultLayoutValues(refComponent: string) {
  let xs, sm, container;
  switch (refComponent) {
    case 'components/@material/informationComponent':
      xs = 'auto';
      sm = 'auto';
      container = false;
      break;
    case 'components/@material/page':
      xs = 12;
      sm = 12;
      container = true;
      break;
    default:
      xs = 12;
      sm = 12;
      container = false;
  }

  return { xs, sm, container };
}

export default LazyComponentLoader;
